import React from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QueryTypeToggle from "./QueryTypeToggle";
import QueryRecommendedFilters from "./QueryRecommendedFilters";
import QueryStatFilterSection from "./QueryStatFilterSection";
import {
  BIO_FILTER_FIELDS,
  DEFAULT_QUERY_PARAMS,
  getStatLabel,
} from "../../utils/queryBuilder";

const Section = ({ title, subtitle, children }) => (
  <Box sx={{ py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Box>
);

const CollapsedSection = ({ title, subtitle, children }) => (
  <Accordion
    defaultExpanded={false}
    disableGutters
    elevation={0}
    sx={{
      bgcolor: "transparent",
      "&:before": { display: "none" },
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0, pb: 2 }}>{children}</AccordionDetails>
  </Accordion>
);

const QueryFilterPanel = ({
  queryParams,
  availableStats,
  availableTeams,
  isLoadingTeams,
  catalog,
  onQueryTypeChange,
  onYearChange,
  onToggleStat,
  onStatFilterChange,
  onBioFilterChange,
  onTeamsChange,
  onSortChange,
  onSortDirectionChange,
  onRecommendedToggle = () => {},
}) => {
  const yearMin = catalog?.yearMin ?? DEFAULT_QUERY_PARAMS.startYear;
  const yearMax = catalog?.yearMax ?? DEFAULT_QUERY_PARAMS.endYear;
  const bioFields = catalog?.bioFields || BIO_FILTER_FIELDS;
  const queryTypes = catalog?.queryTypes;
  const groupId = queryParams.groupId;
  const statsForGroup = catalog?.fieldsByGroup?.[groupId] || availableStats || [];
  const recommendedFilters = catalog?.recommendedByGroup?.[groupId] || undefined;

  const nameSortField = catalog?.nameSortField || "bioData.birthName";

  const catalogTeams = catalog?.teams;
  const teamsOptions = catalogTeams ? catalogTeams : (availableTeams || []);
  const getTeamOptionLabel = (t) => (typeof t === "object" ? (t.label || t.name || t.id) : t);
  const teamsValue = catalogTeams
    ? (queryParams.teams || []).map((id) => catalogTeams.find((t) => t.id === id) || id)
    : (queryParams.teams || []);
  const handleTeamsChange = (_, value) => {
    const ids = value.map((t) => (typeof t === "object" ? t.id : t));
    onTeamsChange(ids);
  };

  const handleYearRangeChange = (_, value) => {
    onYearChange("startYear", value[0]);
    onYearChange("endYear", value[1]);
  };

  return (
    <Box>
      <Section
        title="Type of query"
        subtitle="Career totals, single-season lines, or franchise rollups"
      >
        <QueryTypeToggle
          value={queryParams.queryType}
          onChange={onQueryTypeChange}
          queryTypes={queryTypes}
        />
      </Section>

      {queryParams.queryType !== "team" && (
        <Section title="Recommended for you" subtitle="Quick stat thresholds for common searches">
          <QueryRecommendedFilters
            queryParams={queryParams}
            onToggle={onRecommendedToggle}
            filters={recommendedFilters}
          />
        </Section>
      )}

      {queryParams.queryType !== "team" && (
        <Section title="Season range" subtitle="Optional — limits season-based queries">
          <Box sx={{ px: 1.25, pt: 1, pb: 2 }}>
            <Slider
              value={[queryParams.startYear, queryParams.endYear]}
              min={yearMin}
              max={yearMax}
              onChange={handleYearRangeChange}
              onChangeCommitted={handleYearRangeChange}
              valueLabelDisplay="auto"
              disableSwap
            />
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              type="number"
              label="From"
              value={queryParams.startYear}
              onChange={(e) => onYearChange("startYear", parseInt(e.target.value, 10))}
              inputProps={{ min: yearMin, max: yearMax }}
              sx={{ flex: 1 }}
            />
            <Typography color="text.secondary">to</Typography>
            <TextField
              size="small"
              type="number"
              label="To"
              value={queryParams.endYear}
              onChange={(e) => onYearChange("endYear", parseInt(e.target.value, 10))}
              inputProps={{ min: yearMin, max: yearMax }}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Section>
      )}

      <Section title="Teams" subtitle="Optional — players who played for these teams">
        <Autocomplete
          multiple
          size="small"
          options={teamsOptions}
          value={teamsValue}
          loading={isLoadingTeams}
          getOptionLabel={getTeamOptionLabel}
          isOptionEqualToValue={(opt, val) => {
            const optId = typeof opt === "object" ? opt.id : opt;
            const valId = typeof val === "object" ? val.id : val;
            return optId === valId;
          }}
          onChange={handleTeamsChange}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search teams..." label="Teams" />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={typeof option === "object" ? option.id : option}
                label={getTeamOptionLabel(option)}
                size="small"
              />
            ))
          }
        />
      </Section>

      <Section title="More stats" subtitle="Select stats and set thresholds">
        <QueryStatFilterSection
          availableStats={statsForGroup}
          selectedStats={queryParams.selectedStats}
          statisticalFilters={queryParams.statisticalFilters}
          catalog={catalog}
          onToggleStat={onToggleStat}
          onFilterChange={onStatFilterChange}
        />
      </Section>

      <CollapsedSection title="Player details" subtitle="Optional biographical filters">
        <Stack spacing={1.5}>
          {bioFields.map((field) => (
            <TextField
              key={field.value}
              size="small"
              fullWidth
              label={field.label}
              value={queryParams.biographicalFilters[field.value] || ""}
              onChange={(e) => onBioFilterChange(field.value, e.target.value)}
            />
          ))}
        </Stack>
      </CollapsedSection>

      <CollapsedSection title="Sort" subtitle="Optional result ordering">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <FormControl fullWidth size="small">
            <InputLabel>Sort by</InputLabel>
            <Select
              value={queryParams.sortBy}
              label="Sort by"
              onChange={(e) => onSortChange(e.target.value)}
            >
              <MenuItem value="">No sorting</MenuItem>
              <MenuItem value={nameSortField}>Player name</MenuItem>
              {statsForGroup.map((stat) => (
                <MenuItem key={stat} value={stat}>
                  {getStatLabel(stat, catalog)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Direction</InputLabel>
            <Select
              value={queryParams.sortDirection}
              label="Direction"
              disabled={!queryParams.sortBy}
              onChange={(e) => onSortDirectionChange(Number(e.target.value))}
            >
              <MenuItem value={-1}>Highest first</MenuItem>
              <MenuItem value={1}>Lowest first</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </CollapsedSection>
    </Box>
  );
};

export default QueryFilterPanel;
